import { useStore } from '@/store';
import { getUserApiRequest } from '@/services/api/user-api';
import { getMemberAclApiRequest, getMemberApiRequest } from '@/services/api/user-api';
import type { User, UserWorkspace } from '@/services/models';
import { AppErrorEnum } from '@/services/models/Enums';
import { getCurrenciesApiRequest } from '@/services/api/currency-api';
import {
  getCountriesApiRequest,
  getTimezonesApiRequest,
} from '@/services/api/dictionary-api';
import {
  getBillingAddonsApiRequest,
  getBillingCustomerApiRequest,
  getBillingPlansApiRequest,
  getBillingSubscriptionApiRequest,
} from '@/services/api/billing-api';
import { getUserDataFromLocalStorage } from '@/services/localStorage-users-data';
import { setupWebsocket } from '@/services/utils/websocket';
import { switchDesktopMobileView } from '@/routes/hooks/desktop-view-forced';
import {
  getSupportedDimensionsApiRequest,
  getSupportedMetricsApiRequest,
} from '@/services/api/report-api';
import { getCustomConversionsApiRequest } from '@/services/api/custom-conversions-api';
import {
  getColumnSettingsApiRequest,
  putColumnSettingsApiRequest,
} from '@/services/api/column-settings-api';
import { getColumnsGroupsSettingApiRequest } from '@/services/api/columns-groups-setting-api';
import { GENERAL_PRESET } from '@/services/constants/report';
import { getCustomColumnsApiRequest } from '@/services/api/custom-columns-api';
import { loadLanguageAsync } from '@/i18n';

export const refreshCustomColumns = async () => {
  const store = useStore();
  const customColumns = await getCustomColumnsApiRequest();
  store.setCustomColumns(customColumns);
};

export const getUserFromApi = async (): Promise<User> => {
  const store = useStore();
  try {
    const res = await getUserApiRequest();
    const { workspaces } = res;
    const workspaceIds =
      (workspaces && workspaces.map((workspace: UserWorkspace) => workspace.id)) || [];
    const userDataFromLocalStorage = getUserDataFromLocalStorage(res.id);
    const savedSelectedWorkspaces = userDataFromLocalStorage.selectedWorkspaces || [];
    const savedWorkspaces = userDataFromLocalStorage.workspaces || [];

    const selectedWorkspace = savedWorkspaces
      ? savedSelectedWorkspaces.filter((workspaceId: string) =>
          workspaceIds.includes(workspaceId),
        ) || workspaceIds
      : workspaceIds;

    res.selectedWorkspaces = [...selectedWorkspace];
    store.applyLogin(res);

    if (res.language) {
      await loadLanguageAsync(res.language);
    }

    if (res.hasUnpaidInvoices) {
      store.appError = AppErrorEnum.unpaidInvoices;
    }

    if (userDataFromLocalStorage.desktopViewForced) {
      store.setDesktopViewForced(userDataFromLocalStorage.desktopViewForced);
      switchDesktopMobileView(userDataFromLocalStorage.desktopViewForced);
    }

    return res;
  } catch (error: unknown) {
    const data = (error as { response?: { data?: { message?: unknown } } })?.response
      ?.data;
    const message = typeof data?.message === 'string' ? data.message : null;
    if (message === 'VALIDATE_EMAIL') {
      store.resetForLogout();
      store.applyAuthToken(null);
    }
    throw error;
  }
};

export const refreshUserProfileFromApi = async (): Promise<User> => {
  const store = useStore();
  const user = await getUserFromApi();
  const userDataFromLocalStorage = getUserDataFromLocalStorage(user.id);
  if (userDataFromLocalStorage.bookmarks) {
    store.setBookmarks(userDataFromLocalStorage.bookmarks);
  }
  return user;
};

export const loadProfileData = async () => {
  setupWebsocket();

  const store = useStore();

  try {
    const user = await getUserFromApi();

    const loadColumnSettings = async () => {
      const [columnSettings, customEvents, columnsGroups, customColumns] = await Promise.all([
        getColumnSettingsApiRequest(),
        getCustomConversionsApiRequest(),
        getColumnsGroupsSettingApiRequest(),
        getCustomColumnsApiRequest(),
      ]);

      store.setCustomEvents(customEvents);
      store.setGroupsColumns(columnsGroups || []);
      store.setCustomColumns(customColumns as Parameters<typeof store.setCustomColumns>[0]);

      if (!columnSettings.length) {
        const defaultColumns = Object.values(store.getDefaultColumns()).map(
          (column: unknown) => {
            const columnSettingsItem = column as {
              context?: string;
              width?: Record<string, unknown>;
              order?: unknown;
            };
            const context = columnSettingsItem.context || '';
            const width = columnSettingsItem.width || {};
            return {
              context,
              order: columnSettingsItem.order,
              columns: Object.entries(width).map(([key, value]) => ({
                name: key,
                width: value,
              })),
            };
          },
        );

        await putColumnSettingsApiRequest({
          id: GENERAL_PRESET,
          settings: defaultColumns,
        });

        store.setSavedColumns([
          {
            id: GENERAL_PRESET,
            name: null,
            settings: defaultColumns,
          },
        ]);
        return;
      }

      store.setSavedColumns(columnSettings);
    };

    const loadAcl = async () => {
      if (!user.isMember || !user.id) return;

      const acl = await getMemberAclApiRequest({
        userId: user.id,
        workspaceIds:
          user.workspaces?.map((workspace: UserWorkspace) => workspace.id) || [],
      });
      const aclNormalized = acl.reduce<Record<string, unknown>>((accumulator, item) => {
        const workspaceId = (item as { workspaceId?: string }).workspaceId;
        if (workspaceId) {
          accumulator[workspaceId] = item;
        }
        return accumulator;
      }, {});
      store.acl = aclNormalized as never;

      const member = await getMemberApiRequest(user.id);
      store.member = member;
    };

    const dimensionsPromise = getSupportedDimensionsApiRequest();
    const metricsPromise = getSupportedMetricsApiRequest();
    const catalogPromise = Promise.all([dimensionsPromise, metricsPromise]).then(
      ([dimensions, metrics]) => {
        store.setDimensions(dimensions);
        store.setMetrics(metrics);
        return [dimensions, metrics] as const;
      },
    );

    const baseRequests = [
      catalogPromise,
      catalogPromise.then(() => loadColumnSettings()),
      loadAcl(),
      getCurrenciesApiRequest(),
      getCountriesApiRequest(),
      getTimezonesApiRequest(),
    ];

    const billingRequests: Promise<unknown>[] = [];

    if (!user.isMember) {
      billingRequests.push(
        getBillingPlansApiRequest().then(plans => {
          store.setBillingPlans(plans);
        }),
      );
      billingRequests.push(
        getBillingAddonsApiRequest().then(addons => {
          const addonsById = addons.reduce<Record<string, unknown>>(
            (accumulator, addon) => {
              const addonId = (addon as { id?: string }).id;
              if (addonId) {
                accumulator[addonId] = addon;
              }
              return accumulator;
            },
            {},
          );
          store.setBillingAddons(addonsById);
        }),
      );
    }

    if (user.emailVerified) {
      billingRequests.push(
        Promise.all([
          getBillingSubscriptionApiRequest(),
          getBillingCustomerApiRequest(),
        ]).then(([subscription, customer]) => {
          store.setBilling(subscription);
          store.setBillingCustomer(customer);
        }),
      );
    }

    const [, , , currencyList, countryList, timezoneList] =
      await Promise.all(baseRequests);
    await Promise.all(billingRequests);

    store.setCurrencyList(currencyList);
    store.setCountryList(countryList);
    store.setTimezoneList(timezoneList);
    store.isProfileLoaded = true;

    return {
      user,
      isMember: user.isMember,
      emailVerified: user.emailVerified,
    };
  } catch (error: unknown) {
    throw error;
  }
};
